import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Image as ImageIcon,
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../hooks/use-toast';
import {
  getAllCategories,
  deleteCategory as deleteCategoryService,
  getCategoryProductCount,
  bulkDeleteCategories,
} from '../../services/admin/categories';
import CategoryForm from '../../components/admin/CategoryForm';
import { Checkbox } from '../../components/ui/checkbox';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const CategoriesPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);

  const fetchCategories = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getAllCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (!silent) {
        toast({
          title: 'Hata',
          description: 'Kategoriler yüklenirken bir hata oluştu.',
          variant: 'destructive',
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [toast]);

  useAdminAutoRefresh(fetchCategories, {
    interval: 8000,
    enabled: !isFormOpen,
  });

  const toggleExpand = (categoryId) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const buildCategoryTree = (categoriesList) => {
    const categoryMap = new Map();
    const rootCategories = [];

    // First pass: create map of all categories
    categoriesList.forEach((cat) => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });

    // Second pass: build tree
    categoriesList.forEach((cat) => {
      const categoryNode = categoryMap.get(cat.id);
      if (cat.parentId) {
        const parent = categoryMap.get(cat.parentId);
        if (parent) {
          parent.children.push(categoryNode);
        } else {
          // Parent doesn't exist, treat as root
          rootCategories.push(categoryNode);
        }
      } else {
        rootCategories.push(categoryNode);
      }
    });

    return rootCategories;
  };

  const filterCategories = (categoryTree, searchTerm) => {
    if (!searchTerm) return categoryTree;

    const filtered = [];
    const searchLower = searchTerm.toLowerCase();

    categoryTree.forEach((category) => {
      const matchesName = category.name?.toLowerCase().includes(searchLower);
      const matchingChildren = filterCategories(category.children || [], searchTerm);

      if (matchesName || matchingChildren.length > 0) {
        filtered.push({
          ...category,
          children: matchingChildren,
        });
      }
    });

    return filtered;
  };

  const [productCounts, setProductCounts] = useState({});

  useEffect(() => {
    const fetchProductCounts = async () => {
      const counts = {};
      for (const category of categories) {
        try {
          counts[category.id] = await getCategoryProductCount(category.id);
        } catch (error) {
          console.error(`Error getting count for category ${category.id}:`, error);
          counts[category.id] = 0;
        }
      }
      setProductCounts(counts);
    };

    if (categories.length > 0) {
      fetchProductCounts();
    }
  }, [categories]);

  const getProductCount = (categoryId) => {
    return productCounts[categoryId] || 0;
  };

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setIsFormOpen(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsFormOpen(true);
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setIsDeleteDialogOpen(true);
  };

  const handleToggleCategory = (categoryId) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      await deleteCategoryService(categoryToDelete.id, categoryToDelete.image);
      toast({
        title: 'Başarılı',
        description: 'Kategori başarıyla silindi.',
      });
      setIsDeleteDialogOpen(false);
      setCategoryToDelete(null);
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast({
        title: 'Hata',
        description: 'Kategori silinirken bir hata oluştu. Önce bu kategoriye ait ürünleri kaldırın.',
        variant: 'destructive',
      });
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedCategoryIds.length === 0) return;
    try {
      const result = await bulkDeleteCategories(selectedCategoryIds);
      const deletedIds = result.deletedIds || [];
      setSelectedCategoryIds([]);
      setIsBulkDeleteDialogOpen(false);
      fetchCategories();
      if (deletedIds.length > 0) {
        toast({
          title: 'Başarılı',
          description: `${deletedIds.length} kategori başarıyla silindi.`,
        });
      }
      if (result.errors?.length) {
        toast({
          title: 'Bazı Kategoriler Silinemedi',
          description: result.errors.join(' | '),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error bulk deleting categories:', error);
      toast({
        title: 'Hata',
        description: 'Toplu silme sırasında bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setSelectedCategory(null);
    fetchCategories();
  };

  const categoryTree = buildCategoryTree(categories);
  const filteredTree = filterCategories(categoryTree, searchQuery);

  const flattenTree = (items) => {
    const result = [];
    items.forEach((item) => {
      result.push(item);
      if (item.children?.length) {
        result.push(...flattenTree(item.children));
      }
    });
    return result;
  };
  const visibleCategories = flattenTree(filteredTree);
  const isAllSelected =
    visibleCategories.length > 0 &&
    visibleCategories.every((category) => selectedCategoryIds.includes(category.id));
  const handleToggleAll = () => {
    if (visibleCategories.length === 0) return;
    const allIds = visibleCategories.map((category) => category.id);
    setSelectedCategoryIds(isAllSelected ? [] : allIds);
  };

  const renderCategoryRow = (category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const indent = level * 24;

    return (
      <React.Fragment key={category.id}>
        <tr className="hover:bg-gray-50">
          <td className="px-6 py-4 whitespace-nowrap">
            <Checkbox
              checked={selectedCategoryIds.includes(category.id)}
              onCheckedChange={() => handleToggleCategory(category.id)}
            />
          </td>
          <td className="px-6 py-4 whitespace-nowrap">
            <div className="flex items-center" style={{ paddingLeft: `${indent}px` }}>
              {hasChildren ? (
                <button
                  onClick={() => toggleExpand(category.id)}
                  className="mr-2 text-gray-400 hover:text-gray-600"
                >
                  {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </button>
              ) : (
                <span className="w-6 mr-2" />
              )}
              {category.image ? (
                <img
                  src={category.image}
                  alt={category.name}
                  className="h-12 w-12 object-cover rounded mr-3"
                />
              ) : (
                <div className="h-12 w-12 bg-gray-100 rounded flex items-center justify-center mr-3">
                  <ImageIcon className="text-gray-400" size={20} />
                </div>
              )}
              <div>
                <div className="text-sm font-medium text-gray-900">{category.name}</div>
                {category.slug && (
                  <div className="text-sm text-gray-500">/{category.slug}</div>
                )}
              </div>
            </div>
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {category.parentId
              ? categories.find((c) => c.id === category.parentId)?.name || 'Bilinmiyor'
              : 'Ana Kategori'}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {getProductCount(category.id)}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
            {category.order !== undefined ? category.order : '-'}
          </td>
          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleEditCategory(category)}
              >
                <Edit size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteCategory(category)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          </td>
        </tr>
        {hasChildren && isExpanded && category.children.map((child) => renderCategoryRow(child, level + 1))}
      </React.Fragment>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">Kategori Yönetimi</h1>
            <p className="text-gray-600 mt-1">Kategorileri görüntüleyin, düzenleyin ve yönetin</p>
          </div>
          <div className="admin-toolbar">
            <Button
              variant="outline"
              onClick={() => setIsBulkDeleteDialogOpen(true)}
              disabled={selectedCategoryIds.length === 0}
            >
              <Trash2 size={18} className="mr-2" />
              {selectedCategoryIds.length > 0
                ? `${selectedCategoryIds.length} Kategoriyi Sil`
                : 'Toplu Sil'}
            </Button>
            <Button onClick={handleCreateCategory}>
              <Plus size={18} className="mr-2" />
              Yeni Kategori
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="admin-search-row flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Kategori adı ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {searchQuery && (
            <Button
              variant="ghost"
              onClick={() => setSearchQuery('')}
            >
              <X size={18} className="mr-2" />
              Temizle
            </Button>
          )}
        </div>

        {/* Categories Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        ) : filteredTree.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600 text-lg">
              {searchQuery ? 'Arama sonucu bulunamadı' : 'Henüz kategori eklenmemiş'}
            </p>
            {!searchQuery && (
              <Button onClick={handleCreateCategory} className="mt-4">
                <Plus size={18} className="mr-2" />
                İlk Kategoriyi Ekle
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <Checkbox checked={isAllSelected} onCheckedChange={handleToggleAll} />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Kategori
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Üst Kategori
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ürün Sayısı
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sıra
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTree.map((category) => renderCategoryRow(category, 0))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Category Form Dialog */}
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedCategory ? 'Kategoriyi Düzenle' : 'Yeni Kategori Ekle'}
              </DialogTitle>
              <DialogDescription>
                {selectedCategory
                  ? 'Kategori bilgilerini güncelleyin'
                  : 'Yeni kategori eklemek için bilgileri doldurun'}
              </DialogDescription>
            </DialogHeader>
            <CategoryForm
              category={selectedCategory}
              categories={categories}
              onSuccess={handleFormSuccess}
              onCancel={() => setIsFormOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Kategoriyi Sil</AlertDialogTitle>
              <AlertDialogDescription>
                {categoryToDelete && (
                  <>
                    "{categoryToDelete.name}" kategorisini silmek istediğinizden emin misiniz?
                    Bu kategoriye ait alt kategoriler ve ürünler de etkilenebilir.
                    Bu işlem geri alınamaz.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Delete Confirmation Dialog */}
        <AlertDialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Seçili Kategorileri Sil</AlertDialogTitle>
              <AlertDialogDescription>
                {selectedCategoryIds.length} kategori(yi) silmek istediğinizden emin misiniz?
                Bu işlem geri alınamaz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction onClick={confirmBulkDelete} className="bg-red-600 hover:bg-red-700">
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default CategoriesPage;
